import { type DBSchema, type IDBPDatabase, openDB } from "idb"
import toast from "react-hot-toast"
import { ALL_USERS, createRevocationMarker } from "./revocation-marker"

// ─── "Remember this device" for the private-mode PIN unlock ──────────────────
//
// Opt-in only. When the person ticks "Remember this device" on a successful v2
// PIN unlock we:
//   1. generate a device key: AES-GCM 256, extractable:false, usages
//      wrapKey/unwrapKey only. It is stored in IndexedDB as the CryptoKey
//      OBJECT — structured clone keeps it non-extractable, so no script can
//      read its bytes (exportKey rejects);
//   2. wrapKey("raw") the in-memory DEK under that device key with a fresh
//      12-byte IV. The DEK's raw bytes never surface in JS: WebCrypto wraps
//      it internally. (unwrapDEK already yields an extractable DEK — needed by
//      recovery wrapping — so nothing here makes any key MORE extractable.)
//   3. store { device key, IV, ciphertext, createdAt, expiresAt, version } in
//      IndexedDB, scoped to the user id + household + a fingerprint of the
//      server's PIN-wrapped DEK. Those fields are bound into the AES-GCM
//      additional data, so editing any of them (e.g. pushing expiresAt out)
//      makes the unwrap fail.
//
// Nothing here talks to the server, and nothing stored is the PIN, the KEK,
// the raw DEK or raw device-key bytes. Every failure path fails closed: the
// record is deleted and the caller falls back to the normal PIN prompt.
//
// The vault fingerprint is SHA-256 of the server's `wrappedDek` string (itself
// ciphertext the server already holds). Any PIN reset — e.g. via the recovery
// phrase on ANY device — mints a new salt + wrappedDek, so every remembered
// browser sees a mismatch on its next load and asks for the PIN again. That
// is the only cross-device revocation available: the server keeps no list of
// remembered devices.
//
// Deleting can fail (IndexedDB blocked, quota/IO error, private-mode quirks).
// A retained record must never silently unlock later — in this tab or after a
// reload — so every delete first writes a persistent revocation marker
// (lib/revocation-marker.ts: localStorage, keyed by user id, no key material)
// that is cleared only once the delete is confirmed. While it's set, unlock
// refuses and retries the delete on each load. A failed delete also shows a
// non-blocking notice. Every IndexedDB operation is also time-boxed, so a
// blocked open fails closed instead of hanging lock/logout or the gate.

export const DEVICE_REMEMBER_DAYS = 30
const DEVICE_REMEMBER_MS = DEVICE_REMEMBER_DAYS * 24 * 60 * 60 * 1000
export const DEVICE_RECORD_VERSION = 1

const DB_NAME = "paisa-device-unlock"
const STORE = "remembered"
const REVOKE_PREFIX = "paisa-device-unlock-revoked"
export const FORGET_FAILED_NOTICE = "Couldn’t forget this device; lock again or clear site data."

let idbTimeoutMs = 3000
/** Test hook: shorten the IndexedDB time-box. */
export function __setIdbTimeoutMs(ms: number) {
  idbTimeoutMs = ms
}

export interface DeviceRecord {
  v: number
  userId: string
  householdId: string
  vaultFp: string
  deviceKey: CryptoKey
  iv: Uint8Array<ArrayBuffer>
  ciphertext: ArrayBuffer
  createdAt: number
  expiresAt: number
}

interface DeviceDB extends DBSchema {
  remembered: { key: string; value: DeviceRecord }
}

export interface DeviceScope {
  userId: string
  householdId: string
  /** The server's PIN-wrapped DEK (base64) — only its SHA-256 is stored. */
  wrappedDek: string
}

// ── Revocation markers (fail closed after a failed delete, across reloads) ──

const revoked = createRevocationMarker(REVOKE_PREFIX)

/** true while some record on this browser is marked for deletion (unlock refused). */
export function isDeviceUnlockSuppressed(): boolean {
  return revoked.any()
}

function notifyFailure() {
  try {
    notifyForgetFailed(FORGET_FAILED_NOTICE)
  } catch {
    // A notice failure must never undo the fail-closed marker.
  }
}

let notifyForgetFailed = (message: string) => {
  toast.error(message, { id: "device-forget-failed", duration: 8000 })
}
/** Test hook: observe the "couldn't forget" notice. Returns the previous notifier. */
export function __setForgetFailedNotifier(fn: (message: string) => void) {
  const prev = notifyForgetFailed
  notifyForgetFailed = fn
  return prev
}

/**
 * A marked record never unlocks: retry its delete (this load) and refuse.
 * Returns true when the caller must refuse.
 */
async function refuseIfRevoked(userId: string): Promise<boolean> {
  if (!revoked.isRevoked(userId)) return false
  if (revoked.allRevoked()) await forgetAllDevices()
  else await forgetDevice(userId)
  return true
}

// ── IndexedDB plumbing ───────────────────────────────────────────────────────

function withTimeout<T>(p: Promise<T>, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out`)), idbTimeoutMs)
    p.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      (e) => {
        clearTimeout(t)
        reject(e)
      },
    )
  })
}

async function withDb<T>(label: string, fn: (db: IDBPDatabase<DeviceDB>) => Promise<T>) {
  return withTimeout(
    (async () => {
      const db = await openDB<DeviceDB>(DB_NAME, 1, {
        upgrade(d) {
          d.createObjectStore(STORE, { keyPath: "userId" })
        },
      })
      try {
        return await fn(db)
      } finally {
        db.close()
      }
    })(),
    label,
  )
}

export async function vaultFingerprint(wrappedDek: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(wrappedDek))
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("")
}

function additionalData(
  r: Omit<DeviceRecord, "deviceKey" | "iv" | "ciphertext">,
): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(
    JSON.stringify([r.v, r.userId, r.householdId, r.vaultFp, r.createdAt, r.expiresAt]),
  )
}

function scopeIsComplete(s: DeviceScope): boolean {
  return Boolean(s.userId && s.householdId && s.wrappedDek)
}

/**
 * The one loader: reads this user's record and returns it only if it is
 * structurally valid, unexpired and matches `scope`. An invalid or expired
 * record is DELETED (not just hidden). With an incomplete scope nothing can be
 * judged, so nothing is returned or deleted.
 */
async function loadValidRecord(scope: DeviceScope): Promise<DeviceRecord | null> {
  if (!scopeIsComplete(scope)) return null
  let rec: DeviceRecord | undefined
  try {
    rec = await withDb("read", (db) => db.get(STORE, scope.userId))
  } catch {
    return null
  }
  if (!rec) return null
  const now = Date.now()
  const valid =
    rec.v === DEVICE_RECORD_VERSION &&
    rec.userId === scope.userId &&
    rec.householdId === scope.householdId &&
    rec.vaultFp === (await vaultFingerprint(scope.wrappedDek)) &&
    typeof rec.expiresAt === "number" &&
    typeof rec.createdAt === "number" &&
    now < rec.expiresAt &&
    rec.expiresAt - rec.createdAt <= DEVICE_REMEMBER_MS &&
    rec.deviceKey instanceof CryptoKey &&
    rec.deviceKey.extractable === false &&
    rec.iv instanceof Uint8Array &&
    rec.ciphertext instanceof ArrayBuffer
  if (valid) return rec
  await forgetDevice(scope.userId)
  return null
}

/**
 * Remember `dek` on this browser for DEVICE_REMEMBER_DAYS. Returns true on
 * success. On any error the (possibly partial) record is removed and false is
 * returned — the unlock itself is unaffected.
 */
export async function rememberDevice(dek: CryptoKey, scope: DeviceScope): Promise<boolean> {
  try {
    if (!scopeIsComplete(scope)) throw new Error("incomplete scope")
    // A pending revocation must be confirmed before a new record is written,
    // or the next load's retry would delete it (or worse, a stale one wins).
    if (revoked.isRevoked(scope.userId)) {
      await refuseIfRevoked(scope.userId)
      if (revoked.isRevoked(scope.userId)) return false
    }
    const deviceKey = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false, // extractable: false — the device key's bytes never reach JS
      ["wrapKey", "unwrapKey"],
    )
    const createdAt = Date.now()
    const meta = {
      v: DEVICE_RECORD_VERSION,
      userId: scope.userId,
      householdId: scope.householdId,
      vaultFp: await vaultFingerprint(scope.wrappedDek),
      createdAt,
      expiresAt: createdAt + DEVICE_REMEMBER_MS,
    }
    const iv = crypto.getRandomValues(new Uint8Array(12))
    // wrapKey keeps the DEK bytes inside WebCrypto. It throws if the DEK is
    // non-extractable — we then fail closed rather than weaken the DEK.
    const ciphertext = await crypto.subtle.wrapKey("raw", dek, deviceKey, {
      name: "AES-GCM",
      iv,
      additionalData: additionalData(meta),
    })
    await withDb("write", (db) => db.put(STORE, { ...meta, deviceKey, iv, ciphertext }))
    return true
  } catch {
    await forgetDevice(scope.userId)
    return false
  }
}

/**
 * Try to unlock with this browser's remembered record. Returns the DEK, or
 * null — in which case any invalid record for this user has been deleted
 * (expired, tampered, wrong version/scope, or it simply failed to unwrap).
 * Always null while a revocation marker is set (the delete is retried first).
 */
export async function unlockWithDevice(scope: DeviceScope): Promise<CryptoKey | null> {
  if (await refuseIfRevoked(scope.userId)) return null
  const rec = await loadValidRecord(scope)
  if (!rec) return null
  try {
    // Same shape unwrapDEK produces (extractable, for recovery-phrase
    // regeneration). The device key alone could unwrap it with any flag, so a
    // stricter flag here would add no protection.
    return await crypto.subtle.unwrapKey(
      "raw",
      rec.ciphertext,
      rec.deviceKey,
      { name: "AES-GCM", iv: rec.iv, additionalData: additionalData(rec) },
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"],
    )
  } catch {
    await forgetDevice(scope.userId)
    return null
  }
}

/** When this browser's valid record for `scope` expires, or null. Deletes invalid/expired records. */
export async function getRememberedUntil(scope: DeviceScope): Promise<Date | null> {
  if (await refuseIfRevoked(scope.userId)) return null
  const rec = await loadValidRecord(scope)
  return rec ? new Date(rec.expiresAt) : null
}

/**
 * Forget this browser for one user. Returns false if the delete failed — in
 * which case the persistent revocation marker stays set (unlock refused, the
 * delete retried on each load) and a notice is
 * shown. Never throws.
 */
export async function forgetDevice(userId: string): Promise<boolean> {
  if (!userId) return true
  revoked.mark(userId)
  try {
    await withDb("delete", (db) => db.delete(STORE, userId))
    revoked.clear(userId)
    return true
  } catch {
    notifyFailure()
    return false
  }
}

/**
 * Forget every remembered user on THIS browser (local only). Returns false if
 * the clear failed (the ALL_USERS marker then stays set, with a notice). A
 * successful clear lifts every marker. Never throws.
 */
export async function forgetAllDevices(): Promise<boolean> {
  revoked.mark(ALL_USERS)
  try {
    await withDb("clear", (db) => db.clear(STORE))
    revoked.clearAll()
    return true
  } catch {
    notifyFailure()
    return false
  }
}
